import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { ListItem } from "../types/index.js";
import styles from "./ItemList.module.css";

interface Props {
  items: ListItem[];
  onDelete: (item: ListItem) => void;
  onReorder: (movedId: string, prevId: string | null, nextId: string | null) => void;
}

function SortableItem({ item, onDelete }: { item: ListItem; onDelete: (item: ListItem) => void }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.itemId,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <li ref={setNodeRef} style={style} className={styles.item}>
      <button
        type="button"
        className={styles.handle}
        aria-label={`${item.text}を並べ替え`}
        {...attributes}
        {...listeners}
      >
        &#x2630;
      </button>
      <span className={styles.text}>{item.text}</span>
      <span className={styles.author}>{item.createdByName}</span>
      <button
        type="button"
        className={styles.deleteButton}
        onClick={() => onDelete(item)}
        aria-label={`${item.text}を削除`}
      >
        &times;
      </button>
    </li>
  );
}

export function ItemList({ items, onDelete, onReorder }: Props) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const movedId = active.id as string;
    const overId = over.id as string;

    const oldIndex = items.findIndex((i) => i.itemId === movedId);
    const newIndex = items.findIndex((i) => i.itemId === overId);

    // Compute the new position in the list
    const reordered = [...items];
    const [moved] = reordered.splice(oldIndex, 1);
    reordered.splice(newIndex, 0, moved!);

    const prevItem = newIndex > 0 ? (reordered[newIndex - 1] ?? null) : null;
    const nextItem = newIndex < reordered.length - 1 ? (reordered[newIndex + 1] ?? null) : null;

    onReorder(movedId, prevItem?.itemId ?? null, nextItem?.itemId ?? null);
  };

  if (items.length === 0) {
    return <p className={styles.empty}>アイテムはまだありません。下から追加しましょう！</p>;
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={items.map((i) => i.itemId)} strategy={verticalListSortingStrategy}>
        <ul className={styles.list}>
          {items.map((item) => (
            <SortableItem key={item.itemId} item={item} onDelete={onDelete} />
          ))}
        </ul>
      </SortableContext>
    </DndContext>
  );
}
